import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Building2,
  CalendarClock,
  ExternalLink,
  GraduationCap,
  Layers,
  Sparkles,
  Trophy,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Counter } from '@/components/initiative/Counter';

interface ScaleStat {
  id: string;
  value: number;
  prefix?: string;
  suffix?: string;
  format?: (v: number) => string;
  label: { en: string; ar: string };
  hint: { en: string; ar: string };
  highlight?: boolean;
  icon: typeof Layers;
}

// All figures are non-casualty, ecosystem-scale numbers verified against FIFA,
// Wikipedia (West Bank / Gaza Premier League articles), and the
// national-football-teams.com Palestine listing. Re-verify before each major
// publication — counts shift when leagues add or drop clubs.
const STATS: ScaleStat[] = [
  {
    id: 'clubs',
    value: 53,
    label: {
      en: 'Documented football clubs',
      ar: 'نادي كرة قدم موثَّق',
    },
    hint: {
      en: '4 division levels across West Bank & Gaza',
      ar: '4 مستويات دوري بين الضفة وغزة',
    },
    icon: Trophy,
    highlight: true,
  },
  {
    id: 'top-divisions',
    value: 24,
    label: {
      en: 'Top-flight clubs',
      ar: 'نادي في الدرجة الأولى',
    },
    hint: {
      en: '12 West Bank · 12 Gaza · two Premier Leagues',
      ar: '12 ضفة · 12 غزة · دوريان ممتازان',
    },
    icon: Layers,
  },
  {
    id: 'fifa-projects',
    value: 6,
    label: {
      en: 'FIFA-backed projects delivered',
      ar: 'مشروع منجَز بدعم FIFA',
    },
    hint: {
      en: '2 stadiums · 1 technical centre · 1 futsal · 2 pitches',
      ar: 'ملعبان · مركز فني · صالة · ملعبا تدريب',
    },
    icon: Building2,
    highlight: true,
  },
  {
    id: 'pfa-age',
    value: 1928,
    suffix: '',
    label: {
      en: 'Palestinian FA founded',
      ar: 'تأسيس الاتحاد الفلسطيني',
    },
    hint: {
      en: 'FIFA & AFC member since 1998',
      ar: 'عضو FIFA و AFC منذ 1998',
    },
    icon: CalendarClock,
  },
  {
    id: 'curriculum',
    value: 25,
    label: {
      en: 'Courses curated for the initiative',
      ar: 'كورس مختار للمبادرة',
    },
    hint: {
      en: '4 partners · Barça · Ekkono · ACFC · Champions Hub',
      ar: '4 شركاء · Barça · Ekkono · ACFC · Champions Hub',
    },
    icon: GraduationCap,
    highlight: true,
  },
];

const SOURCES = [
  {
    label: 'FIFA · Palestine member-association impact',
    href: 'https://inside.fifa.com/en/associations/PLE/impact',
  },
  {
    label: 'FIFA · Palestine organisation page',
    href: 'https://inside.fifa.com/en/associations/PLE/organisation',
  },
  {
    label: 'Wikipedia · West Bank Premier League',
    href: 'https://en.wikipedia.org/wiki/West_Bank_Premier_League',
  },
  {
    label: 'Wikipedia · Gaza Strip Premier League',
    href: 'https://en.wikipedia.org/wiki/Gaza_Strip_Premier_League',
  },
  {
    label: 'national-football-teams.com · Palestine clubs',
    href: 'https://www.national-football-teams.com/country/141/2024/Palestine.html',
  },
];

export function SportsImpactAnalytics() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';

  return (
    <section
      id="football-ecosystem"
      className="relative isolate py-24 lg:py-32 bg-primary text-primary-foreground overflow-hidden scroll-mt-20"
    >
      <img
        aria-hidden
        src="/img/initiative/analytics-impact.webp"
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-45 pointer-events-none"
        loading="lazy"
      />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-primary/35 via-primary/25 to-primary/70 pointer-events-none" />
      <div className="absolute inset-0 bg-line-grid opacity-20" />
      <motion.div
        aria-hidden
        animate={{ scale: [1, 1.18, 1], opacity: [0.18, 0.4, 0.18] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -top-24 left-1/3 w-[34rem] h-[34rem] bg-accent/15 rounded-full blur-3xl pointer-events-none"
      />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-6 mb-14 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-8"
          >
            <div className="section-mark text-accent">
              ⚙ {isRTL ? 'نظام كرة القدم الفلسطيني' : 'The football ecosystem'}
            </div>
            <h2
              className={cn(
                'mt-4 font-display font-black uppercase leading-[0.95] text-balance',
                'text-5xl lg:text-6xl xl:text-7xl max-w-4xl',
                isRTL && 'font-display-ar normal-case leading-[1.1]'
              )}
            >
              {isRTL ? (
                <>
                  منظومة جاهزة.{' '}
                  <span className="bg-gradient-to-r from-accent to-accent/60 bg-clip-text text-transparent">
                    تنتظر المدرّبين.
                  </span>
                </>
              ) : (
                <>
                  A system already built.{' '}
                  <span className="bg-gradient-to-r from-accent to-accent/60 bg-clip-text text-transparent">
                    Waiting for coaches.
                  </span>
                </>
              )}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-4 font-editorial-italic text-base lg:text-lg text-primary-foreground/75 leading-relaxed"
          >
            "{isRTL
              ? 'الأندية موجودة، الملاعب موجودة، الاتحاد قائم منذ عقود. ما تحتاجه المنظومة اليوم: مدرّبون مؤهَّلون.'
              : 'The clubs exist, the pitches exist, the federation has been running for nearly a century. What the system needs now is qualified coaches.'}"
          </motion.p>
        </div>

        {/* Stat grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {STATS.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.55,
                  delay: Math.min(idx * 0.08, 0.4),
                  ease: [0.2, 0.8, 0.2, 1],
                }}
                whileHover={{ y: -6 }}
                className={cn(
                  'group relative p-6 lg:p-7 rounded-2xl border-2 backdrop-blur-xl overflow-hidden transition-colors duration-500',
                  stat.highlight
                    ? 'bg-gradient-to-br from-accent/15 via-primary/50 to-primary border-accent/60 shadow-2xl shadow-accent/10'
                    : 'bg-primary-foreground/5 border-primary-foreground/15 hover:border-accent/50'
                )}
              >
                <div
                  aria-hidden
                  className={cn(
                    'absolute -top-16 -right-16 h-44 w-44 rounded-full blur-3xl pointer-events-none transition-opacity duration-500',
                    stat.highlight
                      ? 'bg-accent/30 opacity-100'
                      : 'bg-accent/0 group-hover:bg-accent/15 opacity-0 group-hover:opacity-100'
                  )}
                />

                <div className="relative flex items-center justify-between mb-4">
                  <div
                    className={cn(
                      'h-10 w-10 rounded-xl flex items-center justify-center border',
                      stat.highlight
                        ? 'bg-accent text-primary border-accent shadow-lg shadow-accent/30'
                        : 'bg-accent/10 text-accent border-accent/30'
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={2.2} />
                  </div>
                  <span className="font-mono-mark text-[10px] uppercase tracking-widest text-primary-foreground/55">
                    / 0{idx + 1}
                  </span>
                </div>

                <div className="relative font-display font-black num-tabular leading-[0.85] flex items-baseline gap-1">
                  <span
                    className={cn(
                      'text-5xl lg:text-6xl',
                      stat.highlight
                        ? 'bg-gradient-to-b from-accent via-accent to-accent/70 bg-clip-text text-transparent'
                        : 'text-primary-foreground'
                    )}
                  >
                    {stat.prefix}
                    <Counter value={stat.value} format={stat.format} />
                    {stat.suffix}
                  </span>
                </div>

                <div className="relative mt-4 font-display font-black uppercase text-base lg:text-lg leading-tight text-primary-foreground">
                  {stat.label[lang]}
                </div>
                <div className="relative mt-1.5 text-xs text-primary-foreground/65 leading-relaxed">
                  {stat.hint[lang]}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Initiative response card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.2 }}
          className="mt-10 p-7 lg:p-8 rounded-3xl border-2 border-accent/60 bg-gradient-to-br from-primary-foreground/[0.04] via-primary-foreground/[0.02] to-transparent relative overflow-hidden"
        >
          <motion.div
            aria-hidden
            animate={{ x: ['0%', '100%', '0%'] }}
            transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-y-0 -inset-x-1/2 bg-gradient-to-r from-transparent via-accent/[0.06] to-transparent pointer-events-none"
          />
          <div className="relative grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-7">
              <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-2 inline-flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" />
                {isRTL ? 'استجابة المبادرة' : 'Our response'}
              </div>
              <h3
                className={cn(
                  'font-display font-black uppercase leading-[0.95] text-primary-foreground',
                  'text-3xl lg:text-4xl xl:text-5xl text-balance',
                  isRTL && 'font-display-ar normal-case leading-[1.1]'
                )}
              >
                {isRTL
                  ? '150 مدرب جديد. كورسات معتمدة. مجاناً.'
                  : '150 new coaches. Accredited courses. Free.'}
              </h3>
            </div>
            <div className="md:col-span-5 md:border-s md:border-accent/30 md:ps-6">
              <p className="font-editorial-italic text-base lg:text-lg leading-relaxed text-primary-foreground/85">
                {isRTL
                  ? 'كل مدرب يصل إلى عشرات اللاعبين في الأندية الـ53. 150 مدرب اليوم = آلاف الفرص غداً.'
                  : 'Each coach reaches dozens of players across the 53 clubs. 150 coaches today = thousands of opportunities tomorrow.'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Sources */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 pt-6 border-t border-primary-foreground/15"
        >
          <div className="font-mono-mark text-[10px] uppercase tracking-widest text-primary-foreground/55 mb-3">
            {isRTL ? 'المصادر' : 'Sources'}
          </div>
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-primary-foreground/75">
            {SOURCES.map((src) => (
              <li key={src.href + src.label}>
                <a
                  href={src.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 hover:text-accent transition-colors"
                >
                  <span>{src.label}</span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] font-editorial-italic text-primary-foreground/55 leading-relaxed max-w-3xl">
            {isRTL
              ? 'الأرقام تخص نظام كرة القدم الفلسطيني (الأندية، الدوريات، استثمار FIFA Forward) — تتغيّر مع كل موسم رياضي.'
              : 'Figures describe the standing Palestinian football system — clubs, leagues, FIFA Forward investment — and shift each season. Compiled from FIFA and Wikipedia sources above.'}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
